import React, { useEffect, useMemo, useState } from 'react';
import { adminAPI } from '../../utils/api';
import { FileText, Filter, Search, Eye, Shield, AlertCircle, CheckCircle, User } from 'lucide-react';
import PaginationControls from '../../components/admin/PaginationControls';

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  useEffect(() => {
    loadAuditLogs();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, actionFilter, pageSize]);

  const loadAuditLogs = async () => {
    try {
      const response = await adminAPI.getAuditLogs();
      setLogs(response.data);
    } catch (error) {
      console.error('Failed to load audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = useMemo(() => {
    let filtered = logs;

    if (searchTerm) {
      filtered = filtered.filter((log) =>
        log.actor_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.target?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.details?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (actionFilter) {
      filtered = filtered.filter((log) => log.action === actionFilter);
    }

    return filtered;
  }, [logs, searchTerm, actionFilter]);

  const paginatedLogs = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredLogs.slice(start, start + pageSize);
  }, [filteredLogs, page, pageSize]);

  const getActionIcon = (action) => {
    const icons = {
      admin_login: <Shield className="h-4 w-4" />,
      voucher_redeem: <CheckCircle className="h-4 w-4" />,
      session_authorized: <CheckCircle className="h-4 w-4" />,
      session_terminated: <AlertCircle className="h-4 w-4" />,
      session_kicked: <AlertCircle className="h-4 w-4" />,
      user_disconnect: <AlertCircle className="h-4 w-4" />,
      create_plan: <FileText className="h-4 w-4" />,
      create_user: <User className="h-4 w-4" />,
      create_vouchers: <FileText className="h-4 w-4" />,
      generate_wg_config: <Eye className="h-4 w-4" />,
      archive_plan: <FileText className="h-4 w-4" />,
    };

    return icons[action] || <FileText className="h-4 w-4" />;
  };

  const getActionClass = (action) => {
    if (['voucher_redeem', 'session_authorized'].includes(action)) return 'inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded bg-green-50 text-green-700';
    if (['session_terminated', 'session_kicked', 'user_disconnect'].includes(action)) return 'inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded bg-red-50 text-red-700';
    if (action === 'admin_login') return 'inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded bg-yellow-50 text-yellow-700';
    return 'inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded bg-gray-50 text-gray-700';
  };

  const formatAction = (action) =>
    action
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

  const uniqueActions = [...new Set(logs.map((log) => log.action))];

  return (
    <div className="p-8">
      <div className="bg-white border-b border-gray-200 px-8 py-6 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gray-100 rounded-lg">
            <FileText className="h-5 w-5 text-gray-600" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Audit Logs</h1>
            <p className="text-sm text-gray-600 mt-1">
              Search and filter operational history with paged results instead of one very long screen.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search logs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="relative">
            <Filter className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} className="w-full pl-12 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none">
              <option value="">All Actions</option>
              {uniqueActions.map((action) => (
                <option key={action} value={action}>
                  {formatAction(action)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
          <p className="text-gray-600 mt-4">Loading audit logs...</p>
        </div>
      ) : filteredLogs.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-gray-100 rounded-lg">
              <FileText className="h-8 w-8 text-gray-400" />
            </div>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No logs found</h3>
          <p className="text-gray-600">
            {searchTerm || actionFilter
              ? 'Try adjusting your search or filter to find matching records.'
              : 'Audit entries will appear here when activity is recorded.'}
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {paginatedLogs.map((log) => (
              <div key={log.id} className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="flex min-w-0 gap-4">
                    <div className="p-3 bg-gray-100 rounded-lg shrink-0">{getActionIcon(log.action)}</div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                        <span className={getActionClass(log.action)}>
                          {getActionIcon(log.action)}
                          <span>{formatAction(log.action)}</span>
                        </span>
                        <span className="text-sm text-gray-600">{new Date(log.created_at).toLocaleString()}</span>
                      </div>

                      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
                        <div className="rounded-lg bg-gray-50 px-4 py-3">
                          <p className="text-xs font-semibold uppercase tracking-wider text-gray-600">Actor</p>
                          <p className="mt-2 break-words text-sm font-medium text-gray-900">{log.actor_name || 'System'}</p>
                        </div>

                        {log.target ? (
                          <div className="rounded-lg bg-gray-50 px-4 py-3">
                            <p className="text-xs font-semibold uppercase tracking-wider text-gray-600">Target</p>
                            <p className="mt-2 break-words text-sm font-medium text-gray-900">{log.target}</p>
                          </div>
                        ) : null}

                        {log.ip_address ? (
                          <div className="rounded-lg bg-gray-50 px-4 py-3">
                            <p className="text-xs font-semibold uppercase tracking-wider text-gray-600">IP Address</p>
                            <p className="mt-2 break-all font-mono text-sm text-gray-900">{log.ip_address}</p>
                          </div>
                        ) : null}
                      </div>

                      {log.details ? (
                        <div className="mt-4 rounded-lg bg-white border border-gray-200 px-4 py-4">
                          <p className="text-xs font-semibold uppercase tracking-wider text-gray-600">Details</p>
                          <p className="mt-2 break-words text-sm leading-6 text-gray-900">{log.details}</p>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <PaginationControls
            page={page}
            pageSize={pageSize}
            totalItems={filteredLogs.length}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            itemLabel="logs"
          />
        </>
      )}
    </div>
  );
};

export default AuditLogs;
